// src/pages/conocimiento/ArticuloDetallePage.tsx
import { useState } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Send,
  Archive,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Calendar,
  User,
  Tag,
  Folder,
} from 'lucide-react';
import { Button, Card, Badge } from '@/components/ui';
import {
  useArticulo,
  usePublicarArticulo,
  useArchivarArticulo,
  useDespublicarArticulo,
  useMarcarArticuloUtil,
  useArticulosRelacionados,
} from '@/hooks/queries/useConocimiento';
import {
  TIPO_ARTICULO_LABELS,
  TIPO_ARTICULO_COLORS,
  ESTADO_ARTICULO_LABELS,
  ESTADO_ARTICULO_COLORS,
  AMBITO_ARTICULO_LABELS,
  VISIBILIDAD_ARTICULO_LABELS,
} from '@/types';
import { ArticuloFormModal, VersionesPanel } from './components';

export function ArticuloDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: articulo, isLoading } = useArticulo(id || '');
  const { data: relacionados } = useArticulosRelacionados(id || '');

  const publicarArticulo = usePublicarArticulo();
  const archivarArticulo = useArchivarArticulo();
  const despublicarArticulo = useDespublicarArticulo();
  const marcarUtil = useMarcarArticuloUtil();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!articulo) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">
          Artículo no encontrado
        </h2>
        <Button onClick={() => navigate('/conocimiento')} className="mt-4">
          Volver a la lista
        </Button>
      </div>
    );
  }

  const formatNumber = (numero: number) => `KB-${numero.toString().padStart(4, '0')}`;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const handlePublicar = async () => {
    await publicarArticulo.mutateAsync(articulo.id);
  };

  const handleArchivar = async () => {
    if (confirm('¿Archivar este artículo?')) {
      await archivarArticulo.mutateAsync(articulo.id);
    }
  };

  const handleDespublicar = async () => {
    await despublicarArticulo.mutateAsync(articulo.id);
  };

  const handleMarcarUtil = async (util: boolean) => {
    await marcarUtil.mutateAsync({ id: articulo.id, util });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/conocimiento')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <span className="text-gray-400">|</span>
          <span className="font-mono text-gray-600">
            {formatNumber(articulo.numero)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {articulo.estado === 'BORRADOR' && (
            <Button
              onClick={handlePublicar}
              disabled={publicarArticulo.isPending}
            >
              <Send className="h-4 w-4 mr-2" />
              Publicar
            </Button>
          )}
          {articulo.estado === 'PUBLICADO' && (
            <>
              <Button
                variant="secondary"
                onClick={handleDespublicar}
                disabled={despublicarArticulo.isPending}
              >
                Despublicar
              </Button>
              <Button
                variant="secondary"
                onClick={handleArchivar}
                disabled={archivarArticulo.isPending}
              >
                <Archive className="h-4 w-4 mr-2" />
                Archivar
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={() => setIsEditModalOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Contenido principal */}
        <div className="col-span-2 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Badge className={TIPO_ARTICULO_COLORS[articulo.tipo]}>
                {TIPO_ARTICULO_LABELS[articulo.tipo]}
              </Badge>
              <Badge className={ESTADO_ARTICULO_COLORS[articulo.estado]}>
                {ESTADO_ARTICULO_LABELS[articulo.estado]}
              </Badge>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {articulo.titulo}
            </h1>

            {articulo.resumen && (
              <p className="text-gray-600 mb-6 italic">
                {articulo.resumen}
              </p>
            )}

            <div className="prose max-w-none">
              {articulo.contenido_html ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(articulo.contenido_html) }} />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-gray-800">
                  {articulo.contenido}
                </pre>
              )}
            </div>
          </Card>

          {/* Feedback */}
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                ¿Te resultó útil este artículo?
              </span>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleMarcarUtil(true)}
                  className="flex items-center gap-1 text-gray-600 hover:text-green-600 transition-colors"
                >
                  <ThumbsUp className="h-5 w-5" />
                  <span>{articulo.veces_util}</span>
                </button>
                <button
                  onClick={() => handleMarcarUtil(false)}
                  className="flex items-center gap-1 text-gray-600 hover:text-red-600 transition-colors"
                >
                  <ThumbsDown className="h-5 w-5" />
                  <span>{articulo.veces_no_util}</span>
                </button>
              </div>
            </div>
          </Card>

          {/* Artículos relacionados */}
          {relacionados && relacionados.length > 0 && (
            <Card className="p-4">
              <h3 className="font-medium text-gray-900 mb-3">
                Artículos relacionados
              </h3>
              <div className="space-y-2">
                {relacionados.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/conocimiento/${rel.id}`}
                    className="block p-2 rounded hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-mono text-gray-500 mr-2">
                      {formatNumber(rel.numero)}
                    </span>
                    <span className="text-gray-900">{rel.titulo}</span>
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadatos */}
          <Card className="p-4">
            <h3 className="font-medium text-gray-900 mb-4">Información</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Eye className="h-4 w-4" />
                <span>{articulo.vistas_totales} visualizaciones</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>Creado {formatDate(articulo.created_at)}</span>
              </div>
              {articulo.creado_por && (
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{articulo.creado_por.nombre}</span>
                </div>
              )}
              {articulo.categoria && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Folder className="h-4 w-4" />
                  <span>{articulo.categoria.nombre}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-600">
                <Tag className="h-4 w-4" />
                <span>{AMBITO_ARTICULO_LABELS[articulo.ambito]}</span>
              </div>
              <div className="text-gray-500 text-xs">
                Visibilidad: {VISIBILIDAD_ARTICULO_LABELS[articulo.visibilidad]}
              </div>
              <div className="text-gray-500 text-xs">
                Versión: {articulo.version_actual}
              </div>
            </div>
          </Card>

          {/* Etiquetas */}
          {articulo.etiquetas && articulo.etiquetas.length > 0 && (
            <Card className="p-4">
              <h3 className="font-medium text-gray-900 mb-3">Etiquetas</h3>
              <div className="flex flex-wrap gap-2">
                {articulo.etiquetas.map((etiqueta) => (
                  <Badge
                    key={etiqueta.id}
                    style={{ backgroundColor: etiqueta.color + '20', color: etiqueta.color }}
                  >
                    {etiqueta.nombre}
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {/* Versiones */}
          <Card className="p-4">
            <VersionesPanel articuloId={articulo.id} />
          </Card>
        </div>
      </div>

      <ArticuloFormModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        articulo={articulo}
      />
    </div>
  );
}
